import React, { useEffect, useState } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";

import API from "./config/API";

function FloorForm() {
  const [formData, setFormData] = useState({
    floorName: "",
    floortotalRooms: "",
    pgName: "",
    floorCreatedBy: "",
  });

  const [popup, setPopup] = useState({
    message: "",
    type: "",
  });

  const [isEditable, setIsEditable] = useState(false);

  const navigate = useNavigate();

  // ---------------- POPUP ----------------
  const showPopup = (message, type) => {
    setPopup({ message, type });

    setTimeout(() => {
      setPopup({ message: "", type: "" });
    }, 2000);
  };

  // ---------------- LOAD DATA ----------------
  useEffect(() => {
    const user = JSON.parse(localStorage.getItem("user"));

    if (!user?.emailId || !user?.jwtToken) {
      showPopup("Session Expired. Please login again.", "error");
      navigate("/");
      return;
    }

    const email = user.emailId;
    const token = user.jwtToken;

    setFormData((prev) => ({
      ...prev,
      pgName: user.pgName || "",
      floorCreatedBy: email,
    }));

    axios
      .get(`${API.V1}/floor_rooms/getFloorName`, {
        params: {
          emailId: email,
        },
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      })
      .then((res) => {
        if (res.data.statusCode === 200) {
          const data = res.data.data;

          setFormData((prev) => ({
            ...prev,
            floorName: data.floorName || "",
            floortotalRooms: data.MaxFloorCount || "0",
          }));

          setIsEditable(data.MaxFloorCount === "0");
        } else {
          showPopup(res.data.message, "error");
        }
      })
      .catch((err) => {
        if (err.response?.status === 401) {
          localStorage.clear();
          showPopup("Session Expired. Please login again.", "error");

          setTimeout(() => {
            navigate("/");
          }, 1000);
        } else {
          console.error(err);
          showPopup("Failed to fetch floor details", "error");
        }
      });
  }, [navigate]);

  // ---------------- HANDLE CHANGE ----------------
  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  // ---------------- BACK BUTTON ----------------
  const handleBack = () => {
    navigate("/floorRoomsBeds");
  };

  // ---------------- SUBMIT ----------------
  const handleSubmit = async (e) => {
    e.preventDefault();

    const cleanedFloortotalRooms = formData.floortotalRooms?.includes("/")
      ? formData.floortotalRooms.split("/")[0]
      : formData.floortotalRooms;

    const payload = {
      ...formData,
      floortotalRooms: cleanedFloortotalRooms,
    };

    try {
      const user = JSON.parse(localStorage.getItem("user"));

      const response = await fetch(`${API.V1}/floor_rooms/createFloor`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${user?.jwtToken}`,
        },
        body: JSON.stringify(payload),
      });

      if (response.status === 401) {
        alert("Session Expired. Please login again.");
        localStorage.clear();
        navigate("/");
        return;
      }

      const data = await response.json();

      // SUCCESS
      if (data.statusCode === 200) {
        showPopup(data.message || "Floor created!", "success");

        setTimeout(() => {
          window.location.reload();
        }, 1000);
      }

      // VALIDATION ERROR
      else if (data.statusCode === 400) {
        showPopup(data.message || "Request Failed", "error");

        setTimeout(() => {
          navigate("/floorRoomsBeds");
        }, 1200);
      }

      // OTHER ERRORS
      else {
        showPopup(data.message || "Something went wrong", "error");
      }
    } catch (err) {
      console.error(err);
      showPopup("Server Error", "error");
    }
  };

  return (
    <div style={styles.container}>
      {/* ---------------- BACK BUTTON ---------------- */}
      <button
        type="button"
        onClick={handleBack}
        style={styles.backButton}
        onMouseEnter={(e) => {
          e.currentTarget.style.transform = "translateY(-2px)";
          e.currentTarget.style.boxShadow = "0 8px 20px rgba(0,0,0,0.35)";
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.transform = "translateY(0)";
          e.currentTarget.style.boxShadow = "0 5px 15px rgba(0,0,0,0.25)";
        }}
      >
        ← Back to Floor Rooms & Beds
      </button>

      {/* ---------------- POPUP ---------------- */}
      {popup.message && (
        <div
          style={{
            ...styles.popup,
            background:
              popup.type === "success"
                ? "linear-gradient(135deg,#22c55e,#16a34a)"
                : "linear-gradient(135deg,#ef4444,#dc2626)",
          }}
        >
          {popup.message}
        </div>
      )}

      {/* ---------------- FORM ---------------- */}
      <form style={styles.form} onSubmit={handleSubmit}>
        <h2 style={styles.title}>🏢 Create Floor</h2>

        <div style={styles.formGroup}>
          <label>Floor Name</label>

          <input
            name="floorName"
            value={formData.floorName}
            readOnly
            style={{
              ...styles.input,
              background: "#0f172a",
              color: "#fff",
            }}
          />
        </div>

        <div style={styles.formGroup}>
          <label>Max Floors</label>

          <input
            name="floortotalRooms"
            value={formData.floortotalRooms}
            onChange={handleChange}
            readOnly={!isEditable}
            style={{
              ...styles.input,
              background: isEditable ? "#111827" : "#0f172a",
              color: "#fff",
              cursor: isEditable ? "text" : "not-allowed",
            }}
          />
        </div>

        <div style={styles.formGroup}>
          <label>PG Name</label>

          <input
            name="pgName"
            value={formData.pgName}
            readOnly
            style={{
              ...styles.input,
              background: "#0f172a",
              color: "#fff",
              cursor: "not-allowed",
            }}
          />
        </div>

        <button type="submit" style={styles.button}>
          Create Floor
        </button>
      </form>
    </div>
  );
}

export default FloorForm;

/* ---------------- MODERN STYLES ---------------- */

const styles = {
  container: {
    minHeight: "100vh",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    position: "relative",
    background: "radial-gradient(circle at top,#0f172a,#1e293b,#020617)",
    fontFamily: "Segoe UI, Arial, sans-serif",
    padding: "20px",
    boxSizing: "border-box",
  },

  /* ---------------- BACK BUTTON ---------------- */

  backButton: {
    position: "fixed",
    top: "25px",
    left: "25px",
    padding: "11px 18px",
    borderRadius: "10px",
    border: "1px solid rgba(255,255,255,0.15)",
    background: "rgba(255,255,255,0.08)",
    backdropFilter: "blur(10px)",
    WebkitBackdropFilter: "blur(10px)",
    color: "#fff",
    fontSize: "14px",
    fontWeight: "600",
    cursor: "pointer",
    boxShadow: "0 5px 15px rgba(0,0,0,0.25)",
    transition: "all 0.2s ease",
    zIndex: 10,
  },

  form: {
    width: "380px",
    maxWidth: "100%",
    padding: "30px",
    borderRadius: "16px",
    background: "rgba(255,255,255,0.08)",
    backdropFilter: "blur(12px)",
    WebkitBackdropFilter: "blur(12px)",
    boxShadow: "0 10px 40px rgba(0,0,0,0.4)",
    color: "#fff",
    boxSizing: "border-box",
  },

  title: {
    textAlign: "center",
    marginBottom: "20px",
    fontSize: "20px",
    fontWeight: "600",
  },

  formGroup: {
    marginBottom: "15px",
    display: "flex",
    flexDirection: "column",
    gap: "5px",
  },

  input: {
    width: "100%",
    padding: "10px",
    borderRadius: "10px",
    border: "1px solid rgba(255,255,255,0.2)",
    outline: "none",
    boxSizing: "border-box",
    fontSize: "14px",
  },

  button: {
    width: "100%",
    padding: "12px",
    borderRadius: "10px",
    border: "none",
    background: "linear-gradient(135deg,#3b82f6,#6366f1)",
    color: "#fff",
    fontWeight: "bold",
    cursor: "pointer",
    marginTop: "10px",
    fontSize: "14px",
    transition: "all 0.2s ease",
  },

  popup: {
    position: "fixed",
    top: "20px",
    right: "20px",
    padding: "12px 18px",
    borderRadius: "10px",
    color: "#fff",
    fontWeight: "bold",
    boxShadow: "0 10px 20px rgba(0,0,0,0.3)",
    zIndex: 100,
  },
};
